import React from 'react';
import Sidebar from 'react-sidebar';
import styles from './Header.module.css';
import { Sling as Hamburger } from 'hamburger-react';
import { Link } from 'react-router-dom';

interface HeaderProps {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    toggleMenu: () => void;
    triggerPreload?: () => void;
}

interface MenuItem {
    label: string;
    path: string;
}

interface StickySidebarProps {
    isOpen: boolean;
    onSetOpen: (open: boolean) => void;
    menuItems: MenuItem[];
    socialLinks: { label: string; url: string }[];
    triggerPreload?: () => void;
}

const Header: React.FC<HeaderProps> = ({ isOpen, setIsOpen, toggleMenu, triggerPreload }) => {
    return (
        <div>
            <header className={styles.header}>
                <Link
                    to="/"
                    className={styles.title}
                    style={{ textDecoration: 'none' }}
                    onClick={triggerPreload}
                >
                    L A L O x S O L O R I O
                </Link>
                <button className={styles.bookButton}>BOOK</button>
                <div className={styles.menuBtnContainer}>
                    <Hamburger toggled={isOpen} size={20} toggle={setIsOpen} onToggle={toggleMenu} />
                </div>
            </header>
        </div>
    );
};

// Accessible, dynamic, sticky sidebar
const StickySidebar: React.FC<StickySidebarProps> = ({ isOpen, onSetOpen, menuItems, socialLinks, triggerPreload }) => {
    // Responsive sidebar width calculation
    const [sidebarWidth, setSidebarWidth] = React.useState('250px');

    React.useEffect(() => {
        function handleResize() {
            const width = window.innerWidth;
            if (width <= 600) {
                setSidebarWidth('100vw'); // Full screen on mobile
            } else if (width > 600 && width <= 1024) {
                setSidebarWidth('50vw'); // Half screen on tablets/small laptops
            } else {
                setSidebarWidth('300px'); // Compact on large screens
            }
        }
        handleResize(); // Set on mount
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Keyboard accessibility: close on Escape
    React.useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onSetOpen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onSetOpen]);

    // Focus trap for accessibility
    const sidebarRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        if (isOpen && sidebarRef.current) {
            sidebarRef.current.focus();
        }
    }, [isOpen]);

    const menuContent = (
        <div
            className={styles.menuContent}
            role="menu"
            aria-label="Main menu"
            tabIndex={-1}
            ref={sidebarRef}
        >
          <div className={styles.menuItemsContainer}>
            {menuItems.map((item) => (
                <Link to={item.path} key={item.label} className={styles.menuItem} role="menuitem" tabIndex={0} onClick={() => { triggerPreload && triggerPreload(); onSetOpen(false); }}>
                    {item.label}
                </Link>
            ))}
          </div>
          <div className={styles.sidebarFooterWrapper}>
            <div className={styles.sidebarFooterColumns}>
              <div className={styles.sidebarFooterLeftCol}>
                <a href="https://www.instagram.com/laloxsolorio" className={styles.sidebarFooterSocial} target="_blank" rel="noopener noreferrer" onClick={() => onSetOpen(false)}>INSTAGRAM</a>
                <a href="https://www.facebook.com" className={styles.sidebarFooterSocial} target="_blank" rel="noopener noreferrer" onClick={() => onSetOpen(false)}>FACEBOOK</a>
                <a href="https://www.twitter.com" className={styles.sidebarFooterSocial} target="_blank" rel="noopener noreferrer" onClick={() => onSetOpen(false)}>TWITTER</a>
              </div>
              <div className={styles.sidebarFooterRightCol}>
                <Link to="/book" className={styles.sidebarFooterLink} onClick={() => onSetOpen(false)}>BOOK</Link>
                <Link to="/legal" className={styles.sidebarFooterLink} onClick={() => onSetOpen(false)}>LEGAL</Link>
                <Link to="/faq" className={styles.sidebarFooterLink} onClick={() => onSetOpen(false)}>F.A.Q.</Link>
              </div>
            </div>
            <div className={styles.sidebarFooterCopyright}>
              &copy; {new Date().getFullYear()} Lalo Solorio. All rights reserved.
            </div>
          </div>
        </div>
    );
    return (
        <div id="sidebar-container" className={styles.stickySidebar} style={{ pointerEvents: isOpen ? 'auto' : 'none' }}>
            <Sidebar
                sidebar={menuContent}
                open={isOpen}
                onSetOpen={onSetOpen}
                styles={{ sidebar: { background: 'black', width: sidebarWidth, height: '100%' } }}
                aria-label="Sidebar navigation"
            />
        </div>
    );
};

export { Header, StickySidebar };
export type { StickySidebarProps, HeaderProps };
